import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { slackPickerChannelsQueryOptions } from "@/shared/slackChannelQueries.ts";
import { hasSlackChannelToAdd } from "@/types/slackChannels.ts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SlackChannelPickerForm } from "./SlackChannelPickerForm.tsx";

/**
 * "Add okthx to your channels", opened right after someone connects Slack from
 * Settings or the setup connectors card. A connect from a chat Connect card
 * gets the same picker as a card in that thread instead.
 *
 * Whether to open is decided once, from the first answer: a reconnect where
 * okthx is already in every channel, or a list that can't be read, closes it
 * without showing anything. Deciding again on a later refetch would let the
 * offer appear on its own long after the connect, or vanish mid-pick.
 */
export const SlackChannelPickerDialog = ({
  teamId,
  onClose,
}: {
  teamId: string;
  onClose: () => void;
}) => {
  const { data, isError, isFetching } = useQuery(slackPickerChannelsQueryOptions(teamId));
  const [open, setOpen] = useState(false);
  // Not while a read is in flight: a list cached from before the connect would
  // decide on channels the person has since joined.
  const decided = open || (!isFetching && (data !== undefined || isError));
  const offer = data !== undefined && hasSlackChannelToAdd(data.channels);

  useEffect(() => {
    if (open || !decided) return;
    if (offer) setOpen(true);
    else onClose();
  }, [open, decided, offer, onClose]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-md flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Add okthx to your Slack channels</DialogTitle>
          <DialogDescription>
            okthx can help in the channels it's in. Public channels are checked; pick any private
            ones you want it in too.
          </DialogDescription>
        </DialogHeader>
        <SlackChannelPickerForm
          teamId={teamId}
          onJoined={onClose}
          onCancel={onClose}
          scrollClassName="max-h-[50vh]"
        />
      </DialogContent>
    </Dialog>
  );
};
